// 박별 강세 표시 + 편집 (탭: 강 → 중 → 약 → 무음 → 강)
// 줄바꿈은 beatLayout 규칙: 묶음 단위로만, 고르게, 모든 줄 칸 폭 동일.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { layoutBeats, BEAT_LAYOUT } from './beatLayout';
import { displayGroups } from './timeSignatures';
import { useBeat } from './MetronomeProvider';

const LEVEL_HEIGHT = ['0%', '34%', '67%', '100%'];
const LEVEL_LABEL = ['무음', '약', '중', '강'];

export default function BeatRow({ beats, groups, accents, onChange, playing }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(320);
  const [current, setCurrent] = useState(-1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useBeat((ev) => {
    if (ev.type === 'stop') setCurrent(-1);
    else if (ev.beat !== undefined) setCurrent(ev.beat);
  });

  const { rows, cell } = layoutBeats(displayGroups(beats, groups), width);
  const cycle = (i) => {
    const next = [...accents];
    next[i] = accents[i] === 0 ? 3 : accents[i] - 1;
    onChange(next);
  };

  let index = 0;
  return (
    // 바깥 상자는 폭 측정용, 안쪽 상자는 가운데 놓되 줄은 왼쪽 정렬 → 여러 줄이어도 칸이 세로로 맞는다
    <Box ref={ref} sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
      {rows.map((row, ri) => (
        <Box key={ri} sx={{ display: 'flex', gap: `${BEAT_LAYOUT.groupGap}px` }}>
          {row.map((size, gi) => (
            <Box
              key={gi}
              sx={{
                display: 'flex',
                gap: `${BEAT_LAYOUT.innerGap}px`,
                p: `${BEAT_LAYOUT.groupPad / 2}px`,
                borderRadius: '8px',
                bgcolor: 'action.hover',
              }}
            >
              {Array.from({ length: size }, () => {
                const i = index++;
                const level = accents[i] ?? 1;
                const active = playing && current === i;
                return (
                  <ButtonBase
                    key={i}
                    onClick={() => cycle(i)}
                    aria-label={`${i + 1}박 ${LEVEL_LABEL[level]}`}
                    sx={{
                      width: cell,
                      height: 48,
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: active ? 'secondary.main' : 'divider',
                      outline: active ? '2px solid' : 'none',
                      outlineColor: 'secondary.main',
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: LEVEL_HEIGHT[level],
                        bgcolor: active ? 'secondary.main' : 'text.primary',
                        transition: 'height 0.12s',
                      }}
                    />
                    {level === 0 && (
                      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ width: '45%', height: '2px', bgcolor: 'text.disabled' }} />
                      </Box>
                    )}
                  </ButtonBase>
                );
              })}
            </Box>
          ))}
        </Box>
      ))}
      </Box>
    </Box>
  );
}
